import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Spinner, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../services/AxiosInstance';
import CorporateSubscription from '../user subscriptions/coporatesubscription';
import { clearSubscriptionCache as clearFeatureCache } from '../../utils/featureLock';
import { clearSubscriptionCache } from '../../utils/subscriptionCache';
import { sortSubscriptionPlansBySortOrder } from '../../utils/subscriptionPlansSort';
import './UserSubscription.css';
import './SubscriptionCheckout.css';
import './RegistrationSubscriptionPlans.css';
import { Footer } from '../components';

function getPlanPriceForCycle(plan, cycle) {
  return parseFloat(cycle === 'yearly' ? plan?.yearly_price ?? 0 : plan?.monthly_price ?? 0) || 0;
}

const RegistrationSubscriptionPlans = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const confirmSectionRef = useRef(null);

  const initialModuleTab = useMemo(() => {
    const m = new URLSearchParams(location.search).get('module');
    if (m === 'document' || m === 'task') return m;
    return null;
  }, [location.search]);

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await axiosInstance.get('/subscription/plans');
      if (response.data?.data) {
        return sortSubscriptionPlansBySortOrder([...response.data.data]);
      }
    } catch (error) {
      console.error('Error fetching subscription plans:', error);
    }
    return [];
  };

  useEffect(() => {
    const syncPlanFromUrlOrSession = async () => {
      const urlParams = new URLSearchParams(location.search);
      const planId = urlParams.get('plan');

      if (planId) {
        const plans = await fetchSubscriptionPlans();
        const planToSelect = plans.find((p) => String(p.id) === String(planId));
        if (planToSelect) {
          setSelectedPlan(planToSelect);
          return;
        }
      }

      const sessionSelectedPlan = sessionStorage.getItem('selectedPlan');
      if (sessionSelectedPlan) {
        try {
          const parsed = JSON.parse(sessionSelectedPlan);
          const plans = await fetchSubscriptionPlans();
          const planToSelect = plans.find((p) => p.id === parsed.id);
          if (planToSelect) setSelectedPlan(planToSelect);
        } catch (e) {
          console.error('Error parsing session selected plan:', e);
        }
      }
    };

    syncPlanFromUrlOrSession();
  }, [location.search]);

  useEffect(() => {
    if (!selectedPlan) return;
    const planKey = selectedPlan.id ?? selectedPlan.planId;
    if (planKey == null) return;
    const t = setTimeout(() => {
      confirmSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
    return () => clearTimeout(t);
  }, [selectedPlan?.id, selectedPlan?.planId]);

  const isFreePlan = () =>
    selectedPlan &&
    parseFloat(selectedPlan.monthly_price || 0) === 0 &&
    parseFloat(selectedPlan.yearly_price || 0) === 0;

  const handleProceedToCheckout = () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }
    if (isFreePlan()) {
      toast.error('This plan is free. Use Confirm Free Subscription instead.');
      return;
    }
    const planId = selectedPlan.id || selectedPlan.planId;
    navigate(
      `/subscription/checkout?planId=${planId}&billingCycle=${billingCycle}&checkoutIntent=new`
    );
  };

  const handleSubscribeFree = async () => {
    if (!selectedPlan) {
      toast.error('Please select a subscription plan');
      return;
    }
    if (!isFreePlan()) {
      toast.error('This plan requires payment. Use Proceed to Checkout.');
      return;
    }

    setIsSubscribing(true);
    try {
      await axiosInstance.post('/userSubscription/subscribe', {
        subscription_plan_id: selectedPlan.planId || selectedPlan.id,
      });

      toast.success('Successfully subscribed to the plan!');
      sessionStorage.removeItem('selectedPlan');
      clearSubscriptionCache();
      clearFeatureCache();
      try {
        window.dispatchEvent(new Event('user-subscription-updated'));
      } catch (e) {}
      try {
        const featuresResponse = await axiosInstance.get('/userSubscription/features');
        if (featuresResponse.data?.features) {
          localStorage.setItem('userPlanFeatures', JSON.stringify(featuresResponse.data.features));
        }
      } catch (err) {
        console.error('Error refreshing user features:', err);
      }
      setSelectedPlan(null);
      setTimeout(() => navigate('/task/tasklist'), 1500);
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleSkipForNow = () => {
    sessionStorage.removeItem('selectedPlan');
    toast.info('You can select a subscription plan later from your dashboard');
    navigate('/task/tasklist');
  };

  return (
    <div className="subscription-page registration-subscription-page">
      <div className="row">
        <div className="col-xl-12">
          <div className="row">
            <div className="col-xl-12">
              <div className="row">
                <div className="col-xl-12">
                  <div className="card border-0 shadow-sm main-card">
                    <div className="card-body p-0">
                      <div className="registration-subscription-content-wrap">
                        <div className="registration-subscription-intro">
                          <h4 className="mb-2 fw-bold">Choose your plan</h4>
                          <p className="mb-0">
                            Select a subscription to unlock features. Paid plans go through checkout and the payment
                            gateway. You can skip and subscribe later from My Subscription.
                          </p>
                        </div>

                        <CorporateSubscription
                          activePlanId={null}
                          parentSelectedPlan={selectedPlan}
                          onPlanSelect={(plan) => {
                            setSelectedPlan({ ...plan });
                            try {
                              sessionStorage.setItem('selectedPlan', JSON.stringify(plan));
                            } catch (e) {}
                          }}
                          onBillingCycleChange={setBillingCycle}
                          onModuleTabChange={() => {
                            setSelectedPlan(null);
                            try {
                              sessionStorage.removeItem('selectedPlan');
                            } catch (e) {}
                          }}
                          initialModuleTab={initialModuleTab}
                        />

                      {selectedPlan && (
                        <div ref={confirmSectionRef} className="confirm-section mt-4 mb-4">
                          <div className="w-100">
                            <div className="subscription-confirm-pricing subscription-confirm-detail-layout text-start mb-4 w-100">
                              <Row className="g-3 subscription-confirm-pricing-cols">
                                <Col xs={12} md={6} className="subscription-confirm-col-left">
                                  <div className="checkout-detail-rows">
                                    <div className="checkout-detail-row">
                                      <span>Selected plan:</span>
                                      <span className="fw-semibold checkout-detail-value">
                                        {selectedPlan.PlanName || selectedPlan.display_name || selectedPlan.name}
                                      </span>
                                    </div>
                                    <div className="checkout-detail-row">
                                      <span>Period:</span>
                                      <span className="fw-semibold checkout-detail-value">
                                        {billingCycle === 'yearly' ? '1 year' : '1 month'}
                                      </span>
                                    </div>
                                  </div>
                                </Col>
                                <Col xs={12} md={6} className="subscription-confirm-col-right">
                                  <div className="checkout-detail-rows">
                                    <div className="checkout-detail-row">
                                      <span>Plan amount:</span>
                                      <span className="fw-semibold checkout-detail-value">
                                        {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                                      </span>
                                    </div>
                                  </div>
                                </Col>
                              </Row>
                              <div className="subscription-confirm-total-block w-100">
                                <Row className="g-3 subscription-confirm-total-row">
                                  <Col xs={12} md={6} className="d-none d-md-block" aria-hidden="true" />
                                  <Col xs={12} md={6}>
                                    <div className="checkout-detail-row subscription-confirm-total-inner">
                                      <span className="fw-bold">Total due today:</span>
                                      <span className="fw-bold checkout-detail-value">
                                        {`$${getPlanPriceForCycle(selectedPlan, billingCycle).toFixed(2)}`}
                                      </span>
                                    </div>
                                  </Col>
                                </Row>
                              </div>
                            </div>
                            <div className="text-center">
                              {isFreePlan() ? (
                                <Button
                                  variant="danger"
                                  size="lg"
                                  onClick={handleSubscribeFree}
                                  disabled={isSubscribing}
                                  className="px-5 py-3 confirm-button"
                                >
                                  {isSubscribing ? (
                                    <>
                                      <Spinner animation="border" size="sm" className="me-2" />
                                      Processing...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-check me-2" />
                                      Confirm Free Subscription
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <Button
                                  variant="danger"
                                  size="lg"
                                  onClick={handleProceedToCheckout}
                                  className="px-5 py-3 confirm-button"
                                >
                                  <i className="fas fa-shopping-cart me-2" />
                                  Proceed to Checkout
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                        <div className="text-center pb-3">
                          <Button
                            variant="outline-danger"
                            size="lg"
                            className="px-4 registration-skip-btn"
                            onClick={handleSkipForNow}
                          >
                            <i className="fas fa-clock me-2" />
                            Skip for now
                          </Button>
                        </div>

                        <div className="terms-section-registration pb-4 text-center">
                        <p className="text-muted small mb-0">
                          By continuing, you agree to our{' '}
                          <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                            Terms and Conditions
                          </a>{' '}
                          and{' '}
                          <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
                            Privacy Policy
                          </a>
                          .
                        </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default RegistrationSubscriptionPlans;
